import React, { useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, useTexture } from "@react-three/drei";
import * as THREE from "three";

export default function SingleComponent() {
  /**
   * Sub-component that renders inside the <Canvas>.
   * We can safely use R3F hooks here.
   */
  function CylinderWithCarousel() {
    const gltf = useGLTF("/models/cylendar.glb");

    // Image URLs
    const imageUrls = [
      "/product-pics/1.png",
      "/product-pics/2.png",
      "/product-pics/3.png",
      "/product-pics/4.png",
      "/product-pics/5.png",
      "/product-pics/6.png",
      "/product-pics/7.png",
      "/product-pics/8.png",
      "/product-pics/9.png",
      "/product-pics/10.png",
    ];
    const textures = useTexture(imageUrls);
    const logoTexture = useTexture("/GABS-Logo-schwarz-Slogan-registriert.png");

    // Refs
    const groupRef = useRef();
    const draggingRef = useRef(false);
    const startXRef = useRef(0);
    const rotationVelocityRef = useRef(0);

    // Mouse / Touch down
    const handlePointerDown = (e) => {
      draggingRef.current = true;
      startXRef.current = e.clientX;
    };

    // Mouse / Touch move
    const handlePointerMove = (e) => {
      if (!draggingRef.current || !groupRef.current) return;

      e.stopPropagation();
      // 'preventDefault()' is on the native event:
      e.nativeEvent.preventDefault();

      const deltaX = e.clientX - startXRef.current;
      startXRef.current = e.clientX;

      // Increase or decrease this factor to control rotation sensitivity
      const sensitivity = 0.02; 
      rotationVelocityRef.current = deltaX * sensitivity;
    };

    // Mouse / Touch up
    const handlePointerUp = () => {
      draggingRef.current = false;
    };

    // Every frame, rotate by velocity and apply damping
    useFrame(() => {
      if (groupRef.current) {
        // Apply current velocity
        groupRef.current.rotation.y -= rotationVelocityRef.current;
        // Dampen velocity so rotation slows down if not actively dragging
        rotationVelocityRef.current *= 0.9; // Adjust for more/less friction
      }
    });

    return (
      <group
        ref={groupRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {/* Cylinder Model */}
        <primitive
          object={gltf.scene}
          scale={[3, 3, 3]}
          rotation={[Math.PI / 2, 0, 0]}
          position={[0, -3, 0]}
        />

        {/* Images around the cylinder */}
        {textures.map((texture, idx) => {
          const radius = 4;
          const theta = (idx / textures.length) * Math.PI * 2;
          const x = radius * Math.cos(theta);
          const z = radius * Math.sin(theta);

          return (
            <group key={idx} position={[x, 0, z]} rotation={[0, -theta, 0]}>
              {/* White background */}
              <mesh>
                <planeGeometry args={[2.1, 2.1]} />
                <meshBasicMaterial color="white" />
              </mesh>
              {/* Front side: product image */}
              <mesh position={[0, 0, 0.01]}>
                <planeGeometry args={[2, 2]} />
                <meshBasicMaterial map={texture} />
              </mesh>
              {/* Back side: logo */}
              <mesh position={[0, 0, -0.01]} rotation={[0, Math.PI, 0]}>
                <planeGeometry args={[2, 2]} />
                <meshBasicMaterial map={logoTexture} />
              </mesh>
            </group>
          );
        })}
      </group>
    );
  }

  // The "outer" component that renders the <Canvas>
  return (
    <div
      className="w-full h-screen flex justify-center items-center"
      style={{ touchAction: "pan-y" }}
    >
      <Canvas camera={{ position: [10, 10, 10], fov: 50 }}>
        <ambientLight intensity={1.5} />
        <directionalLight position={[20, 20, 20]} intensity={25} />

        <CylinderWithCarousel />

        {/*
          If you previously used OrbitControls, remove or disable it, 
          otherwise it can interfere with the drag logic. For example:
          
          import { OrbitControls } from "@react-three/drei";
          <OrbitControls enableRotate={false} />
        */}
      </Canvas>
    </div>
  );
}
